import type { ReactNode } from "react";
import { SETUP_STEPS, type SetupStepId } from "./setupSteps.js";

/**
 * The heading each step's content carries when it's rendered inside the Setup
 * page. The progress bar above says where the agent is in the sequence, so this
 * only has to name the step and say what it asks for.
 */
export function SetupStepHeader({
  stepId,
  complete,
  action,
  children,
}: {
  stepId: SetupStepId;
  complete?: boolean;
  /** Primary call to action for the step, pinned to the right on wide screens. */
  action?: ReactNode;
  /** Extra context under the description, e.g. live counts or status notes. */
  children?: ReactNode;
}) {
  const index = SETUP_STEPS.findIndex((entry) => entry.id === stepId);
  const step = SETUP_STEPS[index];
  if (!step) return null;

  return (
    <header className="grid gap-5 border-b-2 border-border-default pb-7 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-content-muted">
            Step {index + 1} · {step.label}
          </p>
          {complete ? (
            <span className="inline-flex items-center rounded-full bg-status-active-bg px-2.5 py-1 text-[11px] font-bold text-status-active-text ring-1 ring-status-active-border">
              <span className="mr-1 h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
              Complete
            </span>
          ) : null}
        </div>
        <h2 className="max-w-4xl font-display text-3xl font-extrabold leading-[0.98] tracking-[-0.045em] text-content-primary sm:text-4xl">
          {step.title}
        </h2>
        <p className="max-w-3xl text-sm font-medium leading-6 text-content-secondary">{step.description}</p>
        {children}
      </div>
      {action}
    </header>
  );
}
